import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  asList,
  createLecture,
  createUnit,
  deleteLecture,
  deleteUnit,
  duplicateLecture,
  duplicateUnit,
  getBank,
  listBanks,
  listLectures,
  listUnits,
  moveLecture,
  moveUnit,
  reorderLectures,
  reorderUnits,
  updateLecture,
  updateUnit,
} from "./api";
import {
  ActionMenu,
  ConfirmDialog,
  CounterPill,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  StatusBadge,
} from "./components/Ui";
import { STATUS, STATUS_OPTIONS } from "./constants";
import { PERMISSIONS, can } from "./permissions";
import { useApiCall } from "./useApiCall";
import { validateLecture, validateUnit } from "./validation";
import "./questionData.css";

const num = (v) => Number(v || 0);
const byOrder = (a, b) =>
  num(a.display_order) - num(b.display_order) ||
  String(a.name || "").localeCompare(String(b.name || ""));

const EMPTY_UNIT = {
  unit_id: "",
  name: "",
  description: "",
  display_order: "",
  status: STATUS.ACTIVE,
};

const EMPTY_LECTURE = {
  lecture_id: "",
  name: "",
  description: "",
  display_order: "",
  status: STATUS.ACTIVE,
};

const BankDetailsPage = () => {
  const { bankId } = useParams();
  const [searchParams] = useSearchParams();
  const focusUnitId = searchParams.get("unit");
  const navigate = useNavigate();
  const call = useApiCall();

  const [bank, setBank] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Lecture data is loaded lazily, per expanded unit.
  const [expanded, setExpanded] = useState({});
  const [lecturesByUnit, setLecturesByUnit] = useState({});
  const [lecturesLoading, setLecturesLoading] = useState({});

  // Unit modal
  const [unitModal, setUnitModal] = useState(null); // { mode, data }
  const [unitErrors, setUnitErrors] = useState({});

  // Lecture modal
  const [lectureModal, setLectureModal] = useState(null); // { mode, unitId, data }
  const [lectureErrors, setLectureErrors] = useState({});

  const [saving, setSaving] = useState(false);

  // Duplicate / move / delete targets
  const [duplicateTarget, setDuplicateTarget] = useState(null); // { level, item }
  const [duplicateName, setDuplicateName] = useState("");
  const [moveTarget, setMoveTarget] = useState(null); // { level, item }
  const [moveDestination, setMoveDestination] = useState("");
  const [moveOptions, setMoveOptions] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { level, item }
  const [busy, setBusy] = useState(false);

  document.title = bank?.name
    ? `${bank.name} | Question Bank`
    : "Question Bank | Medicotoon";

  /* ---------------------------------------------------------------- */
  /* Loading                                                           */
  /* ---------------------------------------------------------------- */
  const fetchUnits = useCallback(async () => {
    const { ok, data } = await call(() => listUnits(bankId), { silent: true });
    if (ok) setUnits(asList(data).rows.slice().sort(byOrder));
    return ok;
  }, [bankId, call]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const bankRes = await call(() => getBank(bankId), { silent: true });
    if (!bankRes.ok) {
      setLoadError(bankRes.message || "Failed to load the question bank");
      setLoading(false);
      return;
    }
    setBank(Array.isArray(bankRes.data) ? bankRes.data[0] : bankRes.data);

    const ok = await fetchUnits();
    if (!ok) setLoadError("Failed to load units");
    setLoading(false);
  }, [bankId, call, fetchUnits]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Deep link (?unit=ID) from a course's Questions button: expand that unit.
  useEffect(() => {
    if (!focusUnitId || loading) return;
    if (!units.some((u) => String(u.unit_id) === String(focusUnitId))) return;
    setExpanded((s) => (s[focusUnitId] ? s : { ...s, [focusUnitId]: true }));
    if (!lecturesByUnit[focusUnitId]) loadLectures(focusUnitId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUnitId, loading, units]);

  const loadLectures = useCallback(
    async (unitId) => {
      setLecturesLoading((s) => ({ ...s, [unitId]: true }));
      const { ok, data } = await call(() => listLectures(unitId), {
        error: "Failed to load lectures",
      });
      if (ok) {
        setLecturesByUnit((s) => ({
          ...s,
          [unitId]: asList(data).rows.slice().sort(byOrder),
        }));
      }
      setLecturesLoading((s) => ({ ...s, [unitId]: false }));
    },
    [call]
  );

  const toggleUnit = (unitId) => {
    const next = !expanded[unitId];
    setExpanded((s) => ({ ...s, [unitId]: next }));
    if (next && !lecturesByUnit[unitId]) loadLectures(unitId);
  };

  const refreshLectures = (unitId) => {
    if (expanded[unitId]) loadLectures(unitId);
    else setLecturesByUnit((s) => ({ ...s, [unitId]: undefined }));
    fetchUnits();
  };

  /* ---------------------------------------------------------------- */
  /* Derived                                                           */
  /* ---------------------------------------------------------------- */
  const visibleUnits = useMemo(() => {
    const term = search.trim().toLowerCase();
    return units.filter((u) => {
      const matchesTerm =
        !term ||
        String(u.name || "").toLowerCase().includes(term) ||
        String(u.description || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" || String(u.status) === statusFilter;
      return matchesTerm && matchesStatus;
    });
  }, [units, search, statusFilter]);

  const totals = useMemo(
    () => ({
      units: units.length,
      lectures: units.reduce((s, u) => s + num(u.lectures_count), 0),
      questions: units.reduce((s, u) => s + num(u.questions_count), 0),
    }),
    [units]
  );

  /* ---------------------------------------------------------------- */
  /* Unit CRUD                                                         */
  /* ---------------------------------------------------------------- */
  const openUnitModal = (mode, unit) => {
    setUnitErrors({});
    setUnitModal({
      mode,
      data:
        mode === "add"
          ? { ...EMPTY_UNIT, display_order: units.length + 1 }
          : {
              unit_id: unit.unit_id,
              name: unit.name || "",
              description: unit.description || "",
              display_order: unit.display_order ?? "",
              status:
                String(unit.status) === STATUS.INACTIVE
                  ? STATUS.INACTIVE
                  : STATUS.ACTIVE,
            },
    });
  };

  const submitUnit = async (e) => {
    e.preventDefault();
    const data = unitModal.data;
    const payload = {
      question_bank_id: bankId,
      name: data.name.trim(),
      description: data.description?.trim() || "",
      display_order: data.display_order === "" ? units.length + 1 : Number(data.display_order),
      status: data.status,
    };

    const validation = validateUnit({ ...payload, display_order: data.display_order });
    setUnitErrors(validation);
    if (Object.keys(validation).length) return;

    setSaving(true);
    const { ok } =
      unitModal.mode === "add"
        ? await call(() => createUnit(payload), { success: "Unit created" })
        : await call(() => updateUnit({ ...payload, unit_id: data.unit_id }), {
            success: "Unit updated",
          });
    setSaving(false);

    if (ok) {
      setUnitModal(null);
      fetchUnits();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Lecture CRUD                                                      */
  /* ---------------------------------------------------------------- */
  const openLectureModal = (mode, unitId, lecture) => {
    setLectureErrors({});
    const existing = lecturesByUnit[unitId] || [];
    setLectureModal({
      mode,
      unitId,
      data:
        mode === "add"
          ? { ...EMPTY_LECTURE, display_order: existing.length + 1 }
          : {
              lecture_id: lecture.lecture_id,
              name: lecture.name || "",
              description: lecture.description || "",
              display_order: lecture.display_order ?? "",
              status:
                String(lecture.status) === STATUS.INACTIVE
                  ? STATUS.INACTIVE
                  : STATUS.ACTIVE,
            },
    });
  };

  const submitLecture = async (e) => {
    e.preventDefault();
    const { data, unitId, mode } = lectureModal;
    const existing = lecturesByUnit[unitId] || [];
    const payload = {
      unit_id: unitId,
      name: data.name.trim(),
      description: data.description?.trim() || "",
      display_order:
        data.display_order === "" ? existing.length + 1 : Number(data.display_order),
      status: data.status,
    };

    const validation = validateLecture({
      ...payload,
      display_order: data.display_order,
    });
    setLectureErrors(validation);
    if (Object.keys(validation).length) return;

    setSaving(true);
    const { ok } =
      mode === "add"
        ? await call(() => createLecture(payload), { success: "Lecture created" })
        : await call(
            () => updateLecture({ ...payload, lecture_id: data.lecture_id }),
            { success: "Lecture updated" }
          );
    setSaving(false);

    if (ok) {
      setLectureModal(null);
      refreshLectures(unitId);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Reorder                                                           */
  /* ---------------------------------------------------------------- */
  const moveUnitOrder = async (unit, direction) => {
    const ordered = [...units];
    const index = ordered.findIndex((u) => u.unit_id === unit.unit_id);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= ordered.length) return;

    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setUnits(ordered.map((u, i) => ({ ...u, display_order: i + 1 })));

    const { ok } = await call(
      () => reorderUnits(bankId, ordered.map((u) => u.unit_id)),
      { error: "Failed to reorder units" }
    );
    if (!ok) fetchUnits();
  };

  const moveLectureOrder = async (unitId, lecture, direction) => {
    const ordered = [...(lecturesByUnit[unitId] || [])];
    const index = ordered.findIndex((l) => l.lecture_id === lecture.lecture_id);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= ordered.length) return;

    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setLecturesByUnit((s) => ({
      ...s,
      [unitId]: ordered.map((l, i) => ({ ...l, display_order: i + 1 })),
    }));

    const { ok } = await call(
      () => reorderLectures(unitId, ordered.map((l) => l.lecture_id)),
      { error: "Failed to reorder lectures" }
    );
    if (!ok) loadLectures(unitId);
  };

  /* ---------------------------------------------------------------- */
  /* Duplicate / move / delete                                         */
  /* ---------------------------------------------------------------- */
  const openDuplicate = (level, item) => {
    setDuplicateTarget({ level, item });
    setDuplicateName(`${item.name} (Copy)`);
  };

  const confirmDuplicate = async () => {
    const { level, item } = duplicateTarget;
    const name = duplicateName.trim();
    if (!name) return;

    setBusy(true);
    const { ok } =
      level === "unit"
        ? await call(() => duplicateUnit(item.unit_id, name), {
            success: "Unit duplicated",
          })
        : await call(() => duplicateLecture(item.lecture_id, name), {
            success: "Lecture duplicated",
          });
    setBusy(false);

    if (ok) {
      setDuplicateTarget(null);
      if (level === "unit") fetchUnits();
      else refreshLectures(item.unit_id);
    }
  };

  const openMove = async (level, item) => {
    setMoveTarget({ level, item });
    setMoveDestination("");
    setMoveOptions([]);

    if (level === "unit") {
      // Destination: any other question bank.
      const { ok, data } = await call(() => listBanks({}), { silent: true });
      if (ok) {
        setMoveOptions(
          asList(data)
            .rows.filter((b) => String(b.question_bank_id) !== String(bankId))
            .map((b) => ({ value: b.question_bank_id, label: b.name }))
        );
      }
    } else {
      // Destination: any other unit in this bank.
      setMoveOptions(
        units
          .filter((u) => String(u.unit_id) !== String(item.unit_id))
          .map((u) => ({ value: u.unit_id, label: u.name }))
      );
    }
  };

  const confirmMove = async () => {
    const { level, item } = moveTarget;
    if (!moveDestination) return;

    setBusy(true);
    const { ok } =
      level === "unit"
        ? await call(() => moveUnit(item.unit_id, moveDestination), {
            success: "Unit moved",
          })
        : await call(() => moveLecture(item.lecture_id, moveDestination), {
            success: "Lecture moved",
          });
    setBusy(false);

    if (ok) {
      setMoveTarget(null);
      if (level === "unit") fetchUnits();
      else {
        refreshLectures(item.unit_id);
        refreshLectures(moveDestination);
      }
    }
  };

  const confirmDelete = async () => {
    const { level, item } = deleteTarget;
    setBusy(true);
    const { ok } =
      level === "unit"
        ? await call(() => deleteUnit(item.unit_id), { success: "Unit deleted" })
        : await call(() => deleteLecture(item.lecture_id), {
            success: "Lecture deleted",
          });
    setBusy(false);

    if (ok) {
      setDeleteTarget(null);
      if (level === "unit") {
        setLecturesByUnit((s) => ({ ...s, [item.unit_id]: undefined }));
        fetchUnits();
      } else {
        refreshLectures(item.unit_id);
      }
    }
  };

  const openLecture = (unit, lecture) =>
    navigate(
      `/question-data/banks/${bankId}/units/${unit.unit_id}/lectures/${lecture.lecture_id}`
    );

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  if (!can(PERMISSIONS.BANK_VIEW)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Question Bank" />
          <EmptyState
            icon="bx bx-lock-alt"
            title="Access denied"
            description="You do not have permission to view question banks."
          />
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Question Data"
            breadcrumbItem={bank?.name || "Question Bank"}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <LoadingState label="Loading question bank..." />
                  ) : loadError ? (
                    <ErrorState message={loadError} onRetry={fetchAll} />
                  ) : (
                    <>
                      <div className="qd-toolbar">
                        <div>
                          <Button
                            color="light"
                            size="sm"
                            className="mb-2"
                            onClick={() => navigate("/question-data/banks")}
                          >
                            <i className="bx bx-arrow-back me-1" />
                            All banks
                          </Button>
                          <h4 className="mb-1">
                            {bank?.name} <StatusBadge status={bank?.status} />
                          </h4>
                          <p className="text-muted mb-2">
                            {bank?.description || "No description"}
                          </p>
                          <div className="qd-toolbar-stats">
                            <CounterPill
                              icon="bx bx-folder"
                              count={totals.units}
                              label="units"
                            />
                            <CounterPill
                              icon="bx bx-book-open"
                              count={totals.lectures}
                              label="lectures"
                            />
                            <CounterPill
                              icon="bx bx-help-circle"
                              count={totals.questions}
                              label="questions in bank"
                            />
                            <span className="qd-counter text-muted">
                              ID: {bankId}
                            </span>
                          </div>
                        </div>

                        <div className="qd-toolbar-actions">
                          {can(PERMISSIONS.UNIT_CREATE) ? (
                            <Button
                              color="primary"
                              onClick={() => openUnitModal("add")}
                            >
                              <i className="bx bx-plus me-1" />
                              Create Unit
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      <Row className="g-2 mt-3">
                        <Col md={6}>
                          <Input
                            type="text"
                            placeholder="Search units..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </Col>
                        <Col md={3}>
                          <Input
                            type="select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            <option value="all">All statuses</option>
                            {STATUS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Input>
                        </Col>
                      </Row>

                      <div className="qd-tree mt-3">
                        {!units.length ? (
                          <EmptyState
                            icon="bx bx-folder-plus"
                            title="No units yet"
                            description="Create the first unit in this bank. You choose the name - there are no fixed unit names."
                            action={
                              can(PERMISSIONS.UNIT_CREATE) ? (
                                <Button
                                  color="primary"
                                  onClick={() => openUnitModal("add")}
                                >
                                  <i className="bx bx-plus me-1" />
                                  Create Unit
                                </Button>
                              ) : null
                            }
                          />
                        ) : !visibleUnits.length ? (
                          <EmptyState
                            icon="bx bx-search"
                            title="No matching units"
                            description="Try a different search term or clear the filters."
                          />
                        ) : (
                          visibleUnits.map((unit, unitIndex) => {
                            const isOpen = !!expanded[unit.unit_id];
                            const lectures = lecturesByUnit[unit.unit_id];
                            return (
                              <div className="qd-node" key={unit.unit_id}>
                                <div
                                  className="qd-node-head"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleUnit(unit.unit_id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") toggleUnit(unit.unit_id);
                                  }}
                                >
                                  <i
                                    className={`bx ${
                                      isOpen ? "bx-chevron-down" : "bx-chevron-right"
                                    } qd-chevron`}
                                  />
                                  <i className="bx bx-folder qd-node-icon" />
                                  <div className="qd-node-title">
                                    <span className="qd-node-name">{unit.name}</span>
                                    {unit.description ? (
                                      <span className="qd-node-desc">
                                        {unit.description}
                                      </span>
                                    ) : null}
                                  </div>

                                  <div className="qd-node-meta">
                                    <CounterPill
                                      count={num(unit.lectures_count)}
                                      label="lectures"
                                    />
                                    <CounterPill
                                      count={num(unit.questions_count)}
                                      label="questions"
                                    />
                                    <StatusBadge status={unit.status} />
                                  </div>

                                  <div
                                    className="qd-node-actions"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {can(PERMISSIONS.UNIT_UPDATE) ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-light"
                                          title="Move up"
                                          disabled={unitIndex === 0}
                                          onClick={() => moveUnitOrder(unit, "up")}
                                        >
                                          <i className="bx bx-up-arrow-alt" />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-light"
                                          title="Move down"
                                          disabled={unitIndex === visibleUnits.length - 1}
                                          onClick={() => moveUnitOrder(unit, "down")}
                                        >
                                          <i className="bx bx-down-arrow-alt" />
                                        </button>
                                      </>
                                    ) : null}
                                    <ActionMenu
                                      actions={[
                                        {
                                          key: "view",
                                          label: isOpen ? "Collapse" : "View lectures",
                                          icon: "bx bx-show",
                                          onClick: () => toggleUnit(unit.unit_id),
                                        },
                                        {
                                          key: "add-lecture",
                                          label: "Create Lecture",
                                          icon: "bx bx-plus",
                                          hidden: !can(PERMISSIONS.LECTURE_CREATE),
                                          onClick: () => {
                                            if (!isOpen) toggleUnit(unit.unit_id);
                                            openLectureModal("add", unit.unit_id);
                                          },
                                        },
                                        {
                                          key: "edit",
                                          label: "Edit / Rename",
                                          icon: "bx bx-edit",
                                          hidden: !can(PERMISSIONS.UNIT_UPDATE),
                                          onClick: () => openUnitModal("edit", unit),
                                        },
                                        {
                                          key: "duplicate",
                                          label: "Duplicate",
                                          icon: "bx bx-copy",
                                          hidden: !can(PERMISSIONS.UNIT_CREATE),
                                          onClick: () => openDuplicate("unit", unit),
                                        },
                                        {
                                          key: "move",
                                          label: "Move to another bank",
                                          icon: "bx bx-transfer",
                                          hidden: !can(PERMISSIONS.UNIT_UPDATE),
                                          onClick: () => openMove("unit", unit),
                                        },
                                        { key: "d1", divider: true },
                                        {
                                          key: "delete",
                                          label: "Delete",
                                          icon: "bx bx-trash",
                                          danger: true,
                                          hidden: !can(PERMISSIONS.UNIT_DELETE),
                                          onClick: () =>
                                            setDeleteTarget({ level: "unit", item: unit }),
                                        },
                                      ]}
                                    />
                                  </div>
                                </div>

                                {isOpen ? (
                                  <div className="qd-children">
                                    {lecturesLoading[unit.unit_id] ? (
                                      <div className="py-3 text-center text-muted">
                                        <Spinner size="sm" className="me-2" />
                                        Loading lectures...
                                      </div>
                                    ) : !lectures ? null : !lectures.length ? (
                                      <div className="qd-empty-inline">
                                        <span className="text-muted">
                                          No lectures in this unit yet.
                                        </span>
                                        {can(PERMISSIONS.LECTURE_CREATE) ? (
                                          <Button
                                            size="sm"
                                            color="primary"
                                            outline
                                            onClick={() =>
                                              openLectureModal("add", unit.unit_id)
                                            }
                                          >
                                            <i className="bx bx-plus me-1" />
                                            Create Lecture
                                          </Button>
                                        ) : null}
                                      </div>
                                    ) : (
                                      <>
                                        {lectures.map((lecture, lectureIndex) => (
                                          <div
                                            className="qd-leaf"
                                            key={lecture.lecture_id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openLecture(unit, lecture)}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter")
                                                openLecture(unit, lecture);
                                            }}
                                          >
                                            <i className="bx bx-book-open qd-node-icon" />
                                            <div className="qd-node-title">
                                              <span className="qd-node-name">
                                                {lecture.name}
                                              </span>
                                              {lecture.description ? (
                                                <span className="qd-node-desc">
                                                  {lecture.description}
                                                </span>
                                              ) : null}
                                            </div>

                                            <div className="qd-node-meta">
                                              <CounterPill
                                                count={num(lecture.questions_count)}
                                                label="questions"
                                              />
                                              <StatusBadge status={lecture.status} />
                                            </div>

                                            <div
                                              className="qd-node-actions"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {can(PERMISSIONS.LECTURE_UPDATE) ? (
                                                <>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-light"
                                                    title="Move up"
                                                    disabled={lectureIndex === 0}
                                                    onClick={() =>
                                                      moveLectureOrder(
                                                        unit.unit_id,
                                                        lecture,
                                                        "up"
                                                      )
                                                    }
                                                  >
                                                    <i className="bx bx-up-arrow-alt" />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-light"
                                                    title="Move down"
                                                    disabled={
                                                      lectureIndex === lectures.length - 1
                                                    }
                                                    onClick={() =>
                                                      moveLectureOrder(
                                                        unit.unit_id,
                                                        lecture,
                                                        "down"
                                                      )
                                                    }
                                                  >
                                                    <i className="bx bx-down-arrow-alt" />
                                                  </button>
                                                </>
                                              ) : null}
                                              <ActionMenu
                                                actions={[
                                                  {
                                                    key: "open",
                                                    label: "Open / Add questions",
                                                    icon: "bx bx-show",
                                                    onClick: () =>
                                                      openLecture(unit, lecture),
                                                  },
                                                  {
                                                    key: "edit",
                                                    label: "Edit / Rename",
                                                    icon: "bx bx-edit",
                                                    hidden: !can(
                                                      PERMISSIONS.LECTURE_UPDATE
                                                    ),
                                                    onClick: () =>
                                                      openLectureModal(
                                                        "edit",
                                                        unit.unit_id,
                                                        lecture
                                                      ),
                                                  },
                                                  {
                                                    key: "duplicate",
                                                    label: "Duplicate",
                                                    icon: "bx bx-copy",
                                                    hidden: !can(
                                                      PERMISSIONS.LECTURE_CREATE
                                                    ),
                                                    onClick: () =>
                                                      openDuplicate("lecture", {
                                                        ...lecture,
                                                        unit_id: unit.unit_id,
                                                      }),
                                                  },
                                                  {
                                                    key: "move",
                                                    label: "Move to another unit",
                                                    icon: "bx bx-transfer",
                                                    hidden: !can(
                                                      PERMISSIONS.LECTURE_UPDATE
                                                    ),
                                                    onClick: () =>
                                                      openMove("lecture", {
                                                        ...lecture,
                                                        unit_id: unit.unit_id,
                                                      }),
                                                  },
                                                  { key: "d1", divider: true },
                                                  {
                                                    key: "delete",
                                                    label: "Delete",
                                                    icon: "bx bx-trash",
                                                    danger: true,
                                                    hidden: !can(
                                                      PERMISSIONS.LECTURE_DELETE
                                                    ),
                                                    onClick: () =>
                                                      setDeleteTarget({
                                                        level: "lecture",
                                                        item: {
                                                          ...lecture,
                                                          unit_id: unit.unit_id,
                                                        },
                                                      }),
                                                  },
                                                ]}
                                              />
                                            </div>
                                          </div>
                                        ))}
                                        {can(PERMISSIONS.LECTURE_CREATE) ? (
                                          <div className="qd-add-inline">
                                            <Button
                                              size="sm"
                                              color="primary"
                                              outline
                                              onClick={() =>
                                                openLectureModal("add", unit.unit_id)
                                              }
                                            >
                                              <i className="bx bx-plus me-1" />
                                              Create Lecture
                                            </Button>
                                          </div>
                                        ) : null}
                                      </>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Unit modal */}
      <Modal
        isOpen={!!unitModal}
        toggle={() => !saving && setUnitModal(null)}
        centered
      >
        <ModalHeader toggle={() => !saving && setUnitModal(null)}>
          {unitModal?.mode === "add" ? "Create Unit" : "Edit Unit"}
        </ModalHeader>
        <ModalBody>
          {unitModal ? (
            <form onSubmit={submitUnit}>
              <Field label="Unit name" required error={unitErrors.name}>
                <Input
                  type="text"
                  autoFocus
                  value={unitModal.data.name}
                  placeholder="Any name you like, e.g. Anatomy or First Semester"
                  invalid={!!unitErrors.name}
                  onChange={(e) =>
                    setUnitModal({
                      ...unitModal,
                      data: { ...unitModal.data, name: e.target.value },
                    })
                  }
                />
              </Field>

              <Field label="Description" hint="Optional">
                <Input
                  type="textarea"
                  rows={3}
                  value={unitModal.data.description}
                  onChange={(e) =>
                    setUnitModal({
                      ...unitModal,
                      data: { ...unitModal.data, description: e.target.value },
                    })
                  }
                />
              </Field>

              <Row>
                <Col md={6}>
                  <Field label="Display order" error={unitErrors.display_order}>
                    <Input
                      type="number"
                      min={0}
                      value={unitModal.data.display_order}
                      onChange={(e) =>
                        setUnitModal({
                          ...unitModal,
                          data: { ...unitModal.data, display_order: e.target.value },
                        })
                      }
                    />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Status">
                    <Input
                      type="select"
                      value={unitModal.data.status}
                      onChange={(e) =>
                        setUnitModal({
                          ...unitModal,
                          data: { ...unitModal.data, status: e.target.value },
                        })
                      }
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </Col>
              </Row>

              {unitModal.mode === "edit" ? (
                <div className="alert alert-info py-2 small">
                  Unit ID <b>{unitModal.data.unit_id}</b> never changes - renaming
                  keeps its lectures, questions and course associations intact.
                </div>
              ) : null}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  type="button"
                  color="light"
                  onClick={() => setUnitModal(null)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {unitModal.mode === "add" ? "Create" : "Save changes"}
                </Button>
              </div>
            </form>
          ) : null}
        </ModalBody>
      </Modal>

      {/* Lecture modal */}
      <Modal
        isOpen={!!lectureModal}
        toggle={() => !saving && setLectureModal(null)}
        centered
      >
        <ModalHeader toggle={() => !saving && setLectureModal(null)}>
          {lectureModal?.mode === "add" ? "Create Lecture" : "Edit Lecture"}
        </ModalHeader>
        <ModalBody>
          {lectureModal ? (
            <form onSubmit={submitLecture}>
              <Field label="Lecture name" required error={lectureErrors.name}>
                <Input
                  type="text"
                  autoFocus
                  value={lectureModal.data.name}
                  placeholder="Any name you like, e.g. Cardiovascular System"
                  invalid={!!lectureErrors.name}
                  onChange={(e) =>
                    setLectureModal({
                      ...lectureModal,
                      data: { ...lectureModal.data, name: e.target.value },
                    })
                  }
                />
              </Field>

              <Field label="Description" hint="Optional">
                <Input
                  type="textarea"
                  rows={3}
                  value={lectureModal.data.description}
                  onChange={(e) =>
                    setLectureModal({
                      ...lectureModal,
                      data: { ...lectureModal.data, description: e.target.value },
                    })
                  }
                />
              </Field>

              <Row>
                <Col md={6}>
                  <Field label="Display order" error={lectureErrors.display_order}>
                    <Input
                      type="number"
                      min={0}
                      value={lectureModal.data.display_order}
                      onChange={(e) =>
                        setLectureModal({
                          ...lectureModal,
                          data: {
                            ...lectureModal.data,
                            display_order: e.target.value,
                          },
                        })
                      }
                    />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Status">
                    <Input
                      type="select"
                      value={lectureModal.data.status}
                      onChange={(e) =>
                        setLectureModal({
                          ...lectureModal,
                          data: { ...lectureModal.data, status: e.target.value },
                        })
                      }
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </Col>
              </Row>

              {lectureModal.mode === "edit" ? (
                <div className="alert alert-info py-2 small">
                  Lecture ID <b>{lectureModal.data.lecture_id}</b> never changes -
                  renaming does not affect its questions.
                </div>
              ) : null}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  type="button"
                  color="light"
                  onClick={() => setLectureModal(null)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {lectureModal.mode === "add" ? "Create" : "Save changes"}
                </Button>
              </div>
            </form>
          ) : null}
        </ModalBody>
      </Modal>

      {/* Duplicate */}
      <Modal
        isOpen={!!duplicateTarget}
        toggle={() => !busy && setDuplicateTarget(null)}
        centered
      >
        <ModalHeader toggle={() => !busy && setDuplicateTarget(null)}>
          Duplicate {duplicateTarget?.level === "unit" ? "Unit" : "Lecture"}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {duplicateTarget?.level === "unit"
              ? "All lectures and questions inside this unit will be copied."
              : "All questions inside this lecture will be copied."}
          </p>
          <Field label="New name" required>
            <Input
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
            />
          </Field>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color="light"
              onClick={() => setDuplicateTarget(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={confirmDuplicate}
              disabled={busy || !duplicateName.trim()}
            >
              {busy ? <Spinner size="sm" className="me-1" /> : null}
              Duplicate
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Move */}
      <Modal isOpen={!!moveTarget} toggle={() => !busy && setMoveTarget(null)} centered>
        <ModalHeader toggle={() => !busy && setMoveTarget(null)}>
          Move {moveTarget?.level === "unit" ? "Unit" : "Lecture"}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Moving <b>{moveTarget?.item?.name}</b> keeps its ID and all of its
            content - only its parent changes.
          </p>
          <Field
            label={
              moveTarget?.level === "unit"
                ? "Destination question bank"
                : "Destination unit"
            }
            required
          >
            <Input
              type="select"
              value={moveDestination}
              onChange={(e) => setMoveDestination(e.target.value)}
            >
              <option value="">Select a destination...</option>
              {moveOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Input>
          </Field>
          {!moveOptions.length ? (
            <div className="alert alert-warning py-2 small mb-0">
              There is no other{" "}
              {moveTarget?.level === "unit" ? "question bank" : "unit"} to move
              this into.
            </div>
          ) : null}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setMoveTarget(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={confirmMove}
              disabled={busy || !moveDestination}
            >
              {busy ? <Spinner size="sm" className="me-1" /> : null}
              Move
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Delete */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={`Delete ${deleteTarget?.level === "unit" ? "unit" : "lecture"}`}
        message={
          deleteTarget?.level === "unit"
            ? `Delete "${deleteTarget?.item?.name}"? Every lecture and question inside it will be removed.`
            : `Delete "${deleteTarget?.item?.name}"? All of its questions will be removed.`
        }
        warning={
          deleteTarget?.level === "unit" && num(deleteTarget?.item?.courses_count) > 0
            ? `This unit is attached to ${num(
                deleteTarget?.item?.courses_count
              )} course(s) and those associations will be removed.`
            : num(deleteTarget?.item?.questions_count) > 0
            ? `${num(deleteTarget?.item?.questions_count)} question(s) will be deleted.`
            : undefined
        }
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default BankDetailsPage;
